<div class="ab-test-manager p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold">
            <i class="fa-solid fa-flask mr-2"></i>
            A/B Testing
        </h2>
        <button class="p-button p-button-primary"
                (click)="showCreateForm = true"
                [disabled]="loading">
            <i class="fa-solid fa-plus mr-2"></i>
            Neuer A/B-Test
        </button>
    </div>

    <!-- Error Message -->
    <div *ngIf="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
        <span>{{ error }}</span>
        <button (click)="clearError()" class="text-red-700 hover:text-red-900">
            <i class="fa-solid fa-times"></i>
        </button>
    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="text-center py-8">
        <i class="fa-solid fa-spinner fa-spin text-2xl"></i>
        <p class="mt-2 text-gray-500">Wird geladen...</p>
    </div>

    <!-- No Tests -->
    <div *ngIf="!loading && tests.length === 0" class="text-center py-8 text-gray-500">
        <i class="fa-solid fa-flask text-4xl mb-4"></i>
        <p>Keine A/B-Tests für diese Kampagne konfiguriert.</p>
        <p class="text-sm mt-2">Klicken Sie auf „Neuer A/B-Test", um zu beginnen.</p>
    </div>

    <!-- Test List and Details -->
    <div *ngIf="!loading && tests.length > 0" class="grid grid-cols-12 gap-4">
        <!-- Test List (Left Panel) -->
        <div class="col-span-4">
            <div class="bg-white rounded-lg shadow">
                <div class="p-3 border-b font-semibold">Tests</div>
                <div class="divide-y">
                    <div *ngFor="let test of tests"
                         class="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                         [class.bg-blue-50]="selectedTest?.id === test.id"
                         (click)="selectTest(test)">
                        <div class="flex justify-between items-start">
                            <div>
                                <div class="font-medium">{{ test.name }}</div>
                                <div class="text-sm text-gray-500">{{ test.description }}</div>
                            </div>
                            <span class="px-2 py-1 text-xs rounded-full"
                                  [ngClass]="getStatusClass(test.status + '')">
                                {{ test.status }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Test Details (Right Panel) -->
        <div class="col-span-8" *ngIf="selectedTest">
            <div class="bg-white rounded-lg shadow">
                <!-- Test Header -->
                <div class="p-4 border-b">
                    <div class="flex justify-between items-center">
                        <div>
                            <h3 class="text-lg font-semibold">{{ selectedTest.name }}</h3>
                            <p class="text-sm text-gray-500">{{ selectedTest.description }}</p>
                        </div>
                        <span class="px-3 py-1 rounded-full"
                              [ngClass]="getStatusClass(selectedTest.status + '')">
                            {{ selectedTest.status }}
                        </span>
                    </div>
                </div>

                <!-- Test Actions -->
                <div class="p-4 border-b bg-gray-50 flex flex-wrap gap-2">
                    <button *ngIf="canStartTest()"
                            class="p-button p-button-success p-button-sm"
                            (click)="startTest()"
                            [disabled]="loading">
                        <i class="fa-solid fa-play mr-1"></i> Test starten
                    </button>
                    <button *ngIf="canPauseTest()"
                            class="p-button p-button-warning p-button-sm"
                            (click)="pauseTest()"
                            [disabled]="loading">
                        <i class="fa-solid fa-pause mr-1"></i> Pausieren
                    </button>
                    <button *ngIf="canResumeTest()"
                            class="p-button p-button-success p-button-sm"
                            (click)="resumeTest()"
                            [disabled]="loading">
                        <i class="fa-solid fa-play mr-1"></i> Fortsetzen
                    </button>
                    <button *ngIf="canEndTest()"
                            class="p-button p-button-info p-button-sm"
                            (click)="endTestForDecision()"
                            [disabled]="loading">
                        <i class="fa-solid fa-stop mr-1"></i> Zur Entscheidung beenden
                    </button>
                    <button *ngIf="canApplyWinner()"
                            class="p-button p-button-primary p-button-sm"
                            (click)="applyWinnerToRemainingMembers()"
                            [disabled]="loading">
                        <i class="fa-solid fa-check-double mr-1"></i> Gewinner auf alle anwenden
                    </button>
                    <button class="p-button p-button-secondary p-button-sm"
                            (click)="refreshResults()"
                            [disabled]="loading">
                        <i class="fa-solid fa-refresh mr-1"></i> Aktualisieren
                    </button>
                    <button class="p-button p-button-danger p-button-sm"
                            (click)="cancelTest()"
                            [disabled]="loading || (selectedTest.status + '' === 'COMPLETED')">
                        <i class="fa-solid fa-ban mr-1"></i> Abbrechen
                    </button>
                </div>

                <!-- Test Groups -->
                <div class="p-4 border-b">
                    <div class="flex justify-between items-center mb-3">
                        <h4 class="font-semibold">Testgruppen</h4>
                        <button *ngIf="(selectedTest.status + '' === 'DRAFT')"
                                class="p-button p-button-outlined p-button-sm"
                                (click)="showAddGroupForm = true">
                            <i class="fa-solid fa-plus mr-1"></i> Gruppe hinzufügen
                        </button>
                    </div>

                    <div *ngIf="testGroups.length === 0" class="text-gray-500 text-sm">
                        Keine Gruppen konfiguriert. Fügen Sie mindestens 2 Gruppen (Kontroll- und Variante) hinzu, um den Test zu starten.
                    </div>

                    <div class="grid grid-cols-2 gap-3">
                        <div *ngFor="let group of testGroups"
                             class="p-3 border rounded-lg">
                            <div class="flex justify-between items-start mb-2">
                                <span class="font-medium">{{ group.name }}</span>
                                <span class="px-2 py-1 text-xs rounded-full"
                                      [ngClass]="getGroupTypeClass(group.groupType + '')">
                                    {{ group.groupType }}
                                </span>
                            </div>
                            <div class="text-sm text-gray-600">
                                <div>Zuteilung: {{ (group.allocationPercentage * 100) | number:'1.0-1' }}%</div>
                                <div>Mitglieder: {{ group.memberCount || 0 }}</div>
                            </div>
                            <button *ngIf="canSelectWinner()"
                                    class="mt-2 p-button p-button-success p-button-sm w-full"
                                    (click)="selectWinner(group.id)">
                                <i class="fa-solid fa-trophy mr-1"></i> Als Gewinner auswählen
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Test Results -->
                <div class="p-4" *ngIf="testResults">
                    <h4 class="font-semibold mb-3">Ergebnisvergleich</h4>

                    <div *ngIf="testResults.recommendation" class="mb-4 p-3 bg-blue-50 rounded-lg text-blue-800">
                        <i class="fa-solid fa-lightbulb mr-2"></i>
                        {{ testResults.recommendation }}
                    </div>

                    <div class="overflow-x-auto">
                        <table class="w-full text-sm">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="p-2 text-left">Gruppe</th>
                                    <th class="p-2 text-right">Mitglieder</th>
                                    <th class="p-2 text-right">Konversionen</th>
                                    <th class="p-2 text-right">Konv.-Rate</th>
                                    <th class="p-2 text-right">Antworten</th>
                                    <th class="p-2 text-right">Antw.-Rate</th>
                                    <th class="p-2 text-center">Signifikant</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let result of testResults.groupResults"
                                    [class.bg-green-50]="result.isLeading"
                                    class="border-b">
                                    <td class="p-2">
                                        <span class="font-medium">{{ result.groupName }}</span>
                                        <span *ngIf="result.isLeading" class="ml-2 text-green-600">
                                            <i class="fa-solid fa-trophy"></i>
                                        </span>
                                    </td>
                                    <td class="p-2 text-right">{{ result.totalMembers }}</td>
                                    <td class="p-2 text-right">{{ result.conversions }}</td>
                                    <td class="p-2 text-right font-medium">{{ result.conversionRate | number:'1.2-2' }}%</td>
                                    <td class="p-2 text-right">{{ result.responses }}</td>
                                    <td class="p-2 text-right">{{ result.responseRate | number:'1.2-2' }}%</td>
                                    <td class="p-2 text-center">
                                        <i *ngIf="result.isStatisticallySignificant"
                                           class="fa-solid fa-check text-green-500"></i>
                                        <i *ngIf="!result.isStatisticallySignificant"
                                           class="fa-solid fa-minus text-gray-400"></i>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="mt-3 text-sm text-gray-500">
                        Zuletzt aktualisiert: {{ testResults.snapshotAt | date:'medium' }}
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Create Test Modal -->
    <div *ngIf="showCreateForm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-lg w-full max-w-md">
            <div class="p-4 border-b flex justify-between items-center">
                <h3 class="text-lg font-semibold">A/B-Test erstellen</h3>
                <button (click)="showCreateForm = false" class="text-gray-500 hover:text-gray-700">
                    <i class="fa-solid fa-times"></i>
                </button>
            </div>
            <div class="p-4 space-y-4">
                <div>
                    <label class="block text-sm font-medium mb-1">Testname *</label>
                    <input type="text" [(ngModel)]="newTestName"
                           class="w-full p-2 border rounded"
                           placeholder="Testname eingeben">
                </div>
                <div>
                    <label class="block text-sm font-medium mb-1">Beschreibung</label>
                    <textarea [(ngModel)]="newTestDescription"
                              class="w-full p-2 border rounded"
                              rows="2"
                              placeholder="Beschreibung eingeben"></textarea>
                </div>
                <div>
                    <label class="block text-sm font-medium mb-1">Testvariable</label>
                    <select [(ngModel)]="newTestVariable" class="w-full p-2 border rounded">
                        <option *ngFor="let v of testVariables" [value]="v.value">{{ v.label }}</option>
                    </select>
                </div>
                <div>
                    <label class="block text-sm font-medium mb-1">Primäre Metrik</label>
                    <select [(ngModel)]="newPrimaryMetric" class="w-full p-2 border rounded">
                        <option *ngFor="let m of primaryMetrics" [value]="m.value">{{ m.label }}</option>
                    </select>
                </div>
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium mb-1">Min. Stichprobengröße</label>
                        <input type="number" [(ngModel)]="newMinimumSampleSize"
                               class="w-full p-2 border rounded"
                               min="10">
                    </div>
                    <div>
                        <label class="block text-sm font-medium mb-1">Konfidenzniveau</label>
                        <input type="number" [(ngModel)]="newConfidenceLevel"
                               class="w-full p-2 border rounded"
                               min="0.5" max="0.99" step="0.01">
                    </div>
                </div>
            </div>
            <div class="p-4 border-t flex justify-end gap-2">
                <button class="p-button p-button-secondary" (click)="showCreateForm = false">Abbrechen</button>
                <button class="p-button p-button-primary" (click)="createTest()" [disabled]="loading">
                    <i *ngIf="loading" class="fa-solid fa-spinner fa-spin mr-1"></i>
                    Test erstellen
                </button>
            </div>
        </div>
    </div>

    <!-- Add Group Modal -->
    <div *ngIf="showAddGroupForm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-lg w-full max-w-md">
            <div class="p-4 border-b flex justify-between items-center">
                <h3 class="text-lg font-semibold">Testgruppe hinzufügen</h3>
                <button (click)="showAddGroupForm = false" class="text-gray-500 hover:text-gray-700">
                    <i class="fa-solid fa-times"></i>
                </button>
            </div>
            <div class="p-4 space-y-4">
                <div>
                    <label class="block text-sm font-medium mb-1">Gruppenname *</label>
                    <input type="text" [(ngModel)]="newGroupName"
                           class="w-full p-2 border rounded"
                           placeholder="z. B. Kontrollgruppe, Variante A">
                </div>
                <div>
                    <label class="block text-sm font-medium mb-1">Gruppentyp</label>
                    <select [(ngModel)]="newGroupType" class="w-full p-2 border rounded">
                        <option *ngFor="let t of groupTypes" [ngValue]="t.value">{{ t.label }}</option>
                    </select>
                </div>
                <div>
                    <label class="block text-sm font-medium mb-1">Zuteilungsprozentsatz</label>
                    <div class="flex items-center gap-2">
                        <input type="range" [(ngModel)]="newAllocationPercentage"
                               class="flex-1"
                               min="1" max="99">
                        <span class="w-16 text-right">{{ newAllocationPercentage }}%</span>
                    </div>
                </div>
                <div>
                    <label class="block text-sm font-medium mb-1">Konfiguration (JSON)</label>
                    <textarea [(ngModel)]="newConfigurationJson"
                              class="w-full p-2 border rounded font-mono text-sm"
                              rows="3"
                              placeholder='{"key": "value"}'></textarea>
                </div>
            </div>
            <div class="p-4 border-t flex justify-end gap-2">
                <button class="p-button p-button-secondary" (click)="showAddGroupForm = false">Abbrechen</button>
                <button class="p-button p-button-primary" (click)="addGroup()" [disabled]="loading">
                    <i *ngIf="loading" class="fa-solid fa-spinner fa-spin mr-1"></i>
                    Gruppe hinzufügen
                </button>
            </div>
        </div>
    </div>
</div>
